import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/authStore';
import { landingPathForRole } from '../../lib/roleRouting';
import {
  LOGIN_BROWSER_KEY,
  LOGIN_EMAIL_POLICY,
  formatWait,
  getFailureCount,
  getLockRemainingMs,
  loginEmailKey,
  registerLoginAttempt,
} from '../../lib/auth/authThrottle';
import { useLockCountdown } from '../../lib/auth/useLockCountdown';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import AuthDivider from '../../components/auth/AuthDivider';
import { Loader2, Eye, EyeOff } from 'lucide-react';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage = () => {
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [suggestReset, setSuggestReset] = useState(false);
  // Verrou synchrone : `isLoading` n'est relu qu'au rendu suivant, un double
  // clic rapide passerait avant.
  const submittingRef = useRef(false);
  const navigate = useNavigate();
  const { signIn, isLoading } = useAuthStore();
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>();

  const email = watch('email') ?? '';
  const lockKeys = email ? [loginEmailKey(email), LOGIN_BROWSER_KEY] : [LOGIN_BROWSER_KEY];
  const { remainingMs, isLocked, refresh: refreshLock } = useLockCountdown(lockKeys);
  
  const onSubmit = async (data: LoginFormData) => {
    if (submittingRef.current) return;
    if (getLockRemainingMs([loginEmailKey(data.email), LOGIN_BROWSER_KEY]) > 0) {
      refreshLock();
      return;
    }
    submittingRef.current = true;
    try {
      setLoginError(null);
      const { error } = await signIn(data.email, data.password);
      const outcome = registerLoginAttempt(data.email, error);
      
      if (outcome.message) {
        setLoginError(outcome.message);
        setSuggestReset(
          outcome.kind === 'invalid_credentials' &&
            getFailureCount(loginEmailKey(data.email)) >= LOGIN_EMAIL_POLICY.threshold,
        );
        refreshLock();
        return;
      }

      // Navigate based on user role.
      // On relit le role dans le store plutot que via la valeur capturee au
      // rendu, que signIn vient de remplacer. Auparavant tout ce qui n'etait
      // pas admin partait vers /dashboard, ce qui envoyait les coordinateurs et
      // les animateurs dans l'espace parent.
      const role = useAuthStore.getState().profile?.role;
      navigate(landingPathForRole(role), { replace: true });
    } catch {
      setLoginError('Une erreur est survenue lors de la connexion');
    } finally {
      submittingRef.current = false;
    }
  };
  
  return (
    <div className="animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Connexion</h2>

      <GoogleSignInButton hint="Utilisez le compte Google qui a la même adresse email que votre compte Éclosion, sinon un nouveau compte vide sera créé." />
      <AuthDivider />
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="email" className="form-label">
            Email
          </label>
          <input
            id="email"
            type="email"
            className={`form-input ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
            {...register('email', {
              required: 'Email requis',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Email invalide',
              },
            })}
          />
          {errors.email && (
            <p className="form-error">{errors.email.message}</p>
          )}
        </div>
        
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="form-label">
              Mot de passe
            </label>
            <Link
              to="/reset-password"
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Mot de passe oublié?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              className={`form-input pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
              {...register('password', {
                required: 'Mot de passe requis',
                minLength: {
                  value: 6,
                  message: 'Le mot de passe doit contenir au moins 6 caractères',
                },
              })}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="form-error">{errors.password.message}</p>
          )}
        </div>
        
        {loginError && (
          <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm" role="alert">
            {loginError}
            {suggestReset && (
              <>
                {' '}
                <Link to="/reset-password" className="underline font-medium">
                  Mot de passe oublié ?
                </Link>
              </>
            )}
          </div>
        )}
        
        <button
          type="submit"
          className="btn-primary w-full py-2.5"
          disabled={isLoading || isLocked}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <Loader2 className="animate-spin mr-2 h-4 w-4" />
              Connexion en cours...
            </span>
          ) : isLocked ? (
            `Réessayez dans ${formatWait(remainingMs)}`
          ) : (
            'Se connecter'
          )}
        </button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-gray-600">
          Pas encore de compte ?{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-800 font-medium">
            S'inscrire
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;